/**
 * The profile, read from the point of view of the person a form is for.
 *
 * The means-assessment forms are completed for ONE person ("the person the assessment is for"),
 * with their partner's details alongside. So "your share" and "your partner's share" depend on who
 * that is: a joint account is 50/50 either way, an account in the partner's name is 0/100 on the
 * client's form and 100/0 on the partner's. Everything here takes `who` and answers accordingly.
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindAsset, FactFindLiability } from '../../../../shared/src/calculation/factFindTypes.ts';

export type Who = 'client' | 'partner';

export const num = (v: unknown): number => {
    const x = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(x) ? x : 0;
};

export const isCouple = (ff: DraftFactFind): boolean => ff?.adviceFor === 'couple';

export function personOf(ff: DraftFactFind, who: Who) {
    const p = (who === 'partner' ? ff?.partner : ff?.client) || {};
    const first = String(p.firstName || '').trim();
    const last = String(p.lastName || '').trim();
    return {
        first,
        last,
        full: [first, last].filter(Boolean).join(' ') || (who === 'partner' ? 'Your partner' : 'You'),
        dob: String(p.dob || ''),
        sex: String((p as { sex?: string }).sex || ''),
        email: String((p as { email?: string }).email || ''),
        phone: String((p as { preferredPhone?: string }).preferredPhone || ''),
        residency: String(p.currentResidency || ''),
    };
}

export const otherOf = (who: Who): Who => (who === 'client' ? 'partner' : 'client');

/** Shares of an owned thing, as the forms ask for them: [you, partner, someone else] in percent. */
export function sharesFor(ownership: unknown, who: Who, couple: boolean): { you: number; partner: number; other: number; entity: string | null } {
    const o = String(ownership ?? '');
    if (o.startsWith('entity:')) return { you: 0, partner: 0, other: 100, entity: o.slice('entity:'.length) };
    if (o === 'joint') return couple ? { you: 50, partner: 50, other: 0, entity: null } : { you: 100, partner: 0, other: 0, entity: null };
    if (o === 'other_entity') return { you: 0, partner: 0, other: 100, entity: null };
    if (o === 'partner') return who === 'partner' ? { you: 100, partner: 0, other: 0, entity: null } : { you: 0, partner: 100, other: 0, entity: null };
    // 'client', blank or unsure: the client's.
    return who === 'client' ? { you: 100, partner: 0, other: 0, entity: null } : { you: 0, partner: 100, other: 0, entity: null };
}

export function shareWords(s: ReturnType<typeof sharesFor>, couple: boolean, structureName?: string): string {
    if (s.entity) return `Owned by ${structureName || 'a trust or company'} (an entity), not personally`;
    const parts = [`your share ${s.you}%`];
    if (couple) parts.push(`partner's share ${s.partner}%`);
    if (s.other) parts.push(`other's share ${s.other}%`);
    return parts.join(', ');
}

export function assetsOf(ff: DraftFactFind, ...types: string[]): FactFindAsset[] {
    const map = (ff?.assets || {}) as Record<string, FactFindAsset[] | undefined>;
    return types.flatMap((t) => (map[t] || []).map((a) => ({ ...a, type: (a.type || t) as FactFindAsset['type'] })));
}

export function liabilitiesOf(ff: DraftFactFind): FactFindLiability[] {
    const map = (ff?.liabilities || {}) as Record<string, FactFindLiability[] | undefined>;
    return Object.entries(map).flatMap(([t, rows]) => (rows || []).map((l) => ({ ...l, type: l.type || t })));
}

/** Loans secured on the home: mortgages linked to it (or to nothing, which the app treats as the home). */
export function homeLoanBalance(ff: DraftFactFind): number {
    return liabilitiesOf(ff)
        .filter((l) => l.type === 'mortgages' && (!l.assetLink || l.assetLink === 'home'))
        .reduce((t, l) => t + num(l.balance ?? l.value), 0);
}

export function loanBalanceFor(ff: DraftFactFind, assetId: string | undefined): number {
    if (!assetId) return 0;
    return liabilitiesOf(ff).filter((l) => String(l.assetLink) === String(assetId)).reduce((t, l) => t + num(l.balance ?? l.value), 0);
}

export function structureName(ff: DraftFactFind, id: string | null): string | undefined {
    if (!id) return undefined;
    return (ff?.structures || []).find((s) => String(s.id) === id)?.name || undefined;
}

/** Gifts recorded in the five financial years up to `asAt`, as the forms' five-year question asks. */
export function giftsInLastFiveYears(ff: DraftFactFind, asAt: Date): Array<{ year: number; amount: number }> {
    const g = (ff?.giftsHistory || {}) as Record<string, unknown>;
    const fyEnd = asAt.getMonth() >= 6 ? asAt.getFullYear() + 1 : asAt.getFullYear();
    return Object.entries(g)
        .map(([k, v]) => ({ m: /^year(\d{4})$/.exec(k), v }))
        .filter((x) => x.m && num(x.v) > 0)
        .map((x) => ({ year: Number(x.m![1]), amount: num(x.v) }))
        .filter((x) => x.year > fyEnd - 5 && x.year <= fyEnd)
        .sort((a, b) => a.year - b.year);
}

/** Does this person (or the couple) own or part-own the home they live in — the forms' home test. */
export function ownsHome(ff: DraftFactFind): boolean {
    const res = ff?.client?.currentResidency;
    return num(ff?.home?.value) > 0 && (res === 'own_home' || res === 'retirement_village' || res === 'aged_care' || !res);
}

/** Who the residential care means assessment is most likely for: the person whose assessment recommends it. */
export function likelyCareEntrant(ff: DraftFactFind): Who {
    if (!isCouple(ff)) return 'client';
    const c = ff?.careNeeds?.clientAcat?.recommendation?.residential;
    const p = ff?.careNeeds?.partnerAcat?.recommendation?.residential;
    return !c && p ? 'partner' : 'client';
}

/** The completer, when someone is filling the profile in for the client. */
export function completerOf(ff: DraftFactFind): { onBehalf: boolean; name: string; relationship: string } {
    const onBehalf = !!ff?.completer && ff.completer !== 'client';
    return {
        onBehalf,
        name: [ff?.completerFirstName, ff?.completerLastName].filter(Boolean).join(' '),
        relationship: String(ff?.completerRelationship || ''),
    };
}
